import { isFunction } from './module';


/**
 * A type definition for either a T or a function returning a T. Optionally, Func<T>s accept an argument of type A1.
 * This type is useful in models/components that want to accept either a value literal or a function returning a value
 * literal, and only care about having something that resolves to a T.
 *
 * E.g.
 * ```
 *   {
 *     text: string | (() => string);
 *   }
 *
 *   ...
 *
 *   let myText: string;
 *   if(isFunction(model.text)) {
 *     myText = model.text();
 *   } else {
 *     myText = model.text;
 *   }
 * ```
 * Becomes
 * ```
 *   {
 *     text: FuncOrVal<string>;
 *   }
 *
 *   ...
 *
 *   const myText = unwrap(model.text);
 * ```
 * This has numerous benefits:
 * * Readability: The `unwrap` call is clearly an assignment; the if-else needs to be read and interpreted as an assignment
 * * Imperative/Declarative: The if-else block instructs unwrapping; the `unwrap` call describes unwrapping
 * * Code-reuse: the unwrap call is physically smaller, reducing bundle size and moving complexity to a single, testable place
 */
export type FuncOrVal<T, A1 = undefined> = T | ((arg: A1) => T);


/**
 * Takes the given input (value or function), and either returns the value as-is, or applies the function to get a value
 * to return. Types have to match -- the accepted args are `T` or `Func<T>`; not `T` or `Func<U>`.
 *
 * Does not enforce that functions who need an argument get that argument. You can have a type that requires a number
 * argument, and call this without an argument, and it will compile.
 *
 * @param toUnwrap - The value or function to unwrap
 * @param arg - The argument to pass to functions when we unwrap them
 */
export const unwrap = <T, A1 = undefined>(
  toUnwrap: FuncOrVal<T, A1>,
  arg?: A1
): T => isFunction(toUnwrap) ? (toUnwrap as ((arg?: A1) => T))(arg) : toUnwrap as T;
